import { Fragment, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Crosshair } from "lucide-react";
import { detectRtl } from "@/shared/lib/rtl";
import type { CompanionPromptDisplay } from "@/shared/lib/events";
import { parseSlashCommandDisplay } from "../../lib/marcusSlashCommands";
import { ToolCallCard } from "./ToolCallCard";
import { MemoryProposalCard } from "./MemoryProposalCard";
import { renderMarkdown } from "./ChatMarkdownRenderer";
import { ThinkingBlock } from "./ChatThinkingIndicators";
import { renderGeneratedAdJobCards } from "./GeneratedAdJobCards";
import {
  isThinkingPart,
  isToolLikePart,
  getToolFallbackResult,
  isRenderableToolOverridePart,
  normalizeToolPart,
  shouldSuppressToolPart,
} from "./chatToolParts";
import {
  isVideoUrl,
  parseAttachedMediaAnnotation,
} from "./chatMarkdownUtils";

export type ChatToolPartRenderer = (
  part: Record<string, unknown>,
  index: number,
) => ReactNode;

function getCompanionCommandDisplay(part: unknown): CompanionPromptDisplay | null {
  const candidate = part as { type?: unknown; data?: unknown };
  if (candidate.type !== "data-companion-command") return null;
  if (!candidate.data || typeof candidate.data !== "object") return null;
  const data = candidate.data as Record<string, unknown>;
  if (data.kind !== "command") return null;
  if (typeof data.label !== "string" || !data.label.trim()) return null;

  return {
    kind: "command",
    label: data.label.trim(),
    ...(typeof data.description === "string" && data.description.trim()
      ? { description: data.description.trim() }
      : {}),
    ...(typeof data.source === "string" && data.source.trim()
      ? { source: data.source.trim() }
      : {}),
  };
}

function CompanionCommandBubble({
  display,
  compact,
}: {
  display: CompanionPromptDisplay;
  compact?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className={`ml-auto inline-flex min-w-0 items-center gap-2 rounded-xl rounded-br-sm border border-primary/25 bg-primary/10 text-left text-foreground shadow-sm ${
        compact ? "max-w-[80%] px-2.5 py-2" : "max-w-full px-3.5 py-3"
      }`}
    >
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary ${
          compact ? "h-6 w-6" : "h-7 w-7"
        }`}
      >
        <Crosshair className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-normal text-primary/80">
          {display.source ?? "Command sent"}
        </span>
        <span
          className={`block break-words font-semibold [overflow-wrap:anywhere] ${
            compact ? "text-[12px]" : "text-sm"
          }`}
        >
          {display.label}
        </span>
        {display.description && (
          <span
            className={`mt-0.5 block break-words text-muted-foreground [overflow-wrap:anywhere] ${
              compact ? "text-[10px] leading-snug" : "text-xs leading-snug"
            }`}
          >
            {display.description}
          </span>
        )}
      </span>
    </motion.div>
  );
}

function AttachedMediaPreview({
  urls,
  isUser,
  compact,
}: {
  urls: string[];
  isUser: boolean;
  compact: boolean;
}) {
  if (urls.length === 0) return null;
  const wrapperClass = compact
    ? `flex flex-wrap gap-1.5 max-w-[80%] ${isUser ? "ml-auto" : "mr-auto"}`
    : "flex flex-wrap gap-2";
  const itemClass = compact ? "rounded-xl overflow-hidden" : "rounded-2xl overflow-hidden";
  const mediaClass = compact
    ? "max-w-full max-h-32 rounded-xl border border-border/30 object-cover"
    : "max-w-full max-h-48 rounded-2xl border border-border/30 object-cover";

  return (
    <div className={wrapperClass}>
      {urls.map((url, idx) => (
        <div key={idx} className={itemClass}>
          {isVideoUrl(url) ? (
            <video
              src={url}
              controls
              className={mediaClass}
              preload="metadata"
            >
              <track kind="captions" />
            </video>
          ) : (
            <img
              src={url}
              alt="Attached media"
              className={mediaClass}
              loading="lazy"
            />
          )}
        </div>
      ))}
    </div>
  );
}

function renderTextMessagePart({
  fragmentKey,
  text,
  isUser,
  compact,
  mediaAutoPreview,
}: {
  fragmentKey: number;
  text: string;
  isUser: boolean;
  compact: boolean;
  mediaAutoPreview?: boolean;
}) {
  const { displayText, attachedMediaUrls } = parseAttachedMediaAnnotation(text);
  if (!displayText.trim() && attachedMediaUrls.length === 0) return null;

  const bubbleClass = compact
    ? `min-w-0 overflow-hidden rounded-xl break-words [overflow-wrap:anywhere] ${
        isUser
          ? "max-w-[80%] px-3 py-2 text-[12px] font-medium leading-relaxed"
          : "max-w-[92%] px-3.5 py-2.5 text-[13px] font-normal leading-6"
      } ${
        isUser
          ? "ml-auto bg-primary text-primary-foreground rounded-br-sm"
          : "mr-auto bg-muted/50 text-foreground rounded-bl-sm border border-border/20"
      } ${detectRtl(displayText) ? "font-[Vazirmatn] text-right" : ""}`
    : `px-4 py-3 rounded-2xl text-[15px] leading-relaxed shadow-sm ${
        isUser
          ? "bg-primary text-primary-foreground rounded-br-sm"
          : "bg-card border border-border/50 text-card-foreground rounded-bl-sm"
      } ${detectRtl(displayText) ? "font-[Vazirmatn] text-right" : ""}`;

  return (
    <Fragment key={fragmentKey}>
      <AttachedMediaPreview urls={attachedMediaUrls} isUser={isUser} compact={compact} />
      {displayText.trim() && (
        <div dir="auto" className={bubbleClass}>
          {renderMarkdown(displayText, {
            compact,
            mediaAutoPreview,
            skipMediaUrls: attachedMediaUrls,
          })}
        </div>
      )}
    </Fragment>
  );
}

export function renderChatMessagePart({
  part,
  index,
  isUser,
  compact,
  mediaAutoPreview,
  renderToolPart,
}: {
  part: unknown;
  index: number;
  isUser: boolean;
  compact: boolean;
  mediaAutoPreview?: boolean;
  renderToolPart?: ChatToolPartRenderer;
}) {
  const commandDisplay = getCompanionCommandDisplay(part);
  if (commandDisplay) {
    return (
      <CompanionCommandBubble
        key={index}
        display={commandDisplay}
        compact={compact}
      />
    );
  }

  const text = (part as { type?: unknown; text?: unknown }).text;
  if ((part as { type?: unknown }).type === "text" && typeof text === "string" && text) {
    // Rehydrated history brings hidden slash prompts back as raw text (the
    // `data-companion-command` pill part lives only in live local state).
    // Re-render the pill so the `[MARCUS_SLASH_V1] …` prompt never surfaces.
    if (isUser) {
      const slashDisplay = parseSlashCommandDisplay(text);
      if (slashDisplay) {
        return <CompanionCommandBubble key={index} display={slashDisplay} compact={compact} />;
      }
    }
    return renderTextMessagePart({
      fragmentKey: index,
      text,
      isUser,
      compact,
      mediaAutoPreview,
    });
  }

  if (isThinkingPart(part)) {
    return <ThinkingBlock key={index} compact={compact} />;
  }

  if (!isToolLikePart(part)) return null;

  const normalizedPart = normalizeToolPart(part);
  if (shouldSuppressToolPart(normalizedPart)) return null;

  const generatedAdCard = renderGeneratedAdJobCards(normalizedPart, index);
  if (generatedAdCard) return generatedAdCard;

  if (renderToolPart && isRenderableToolOverridePart(normalizedPart, compact)) {
    return (
      <Fragment key={index}>
        {renderToolPart(normalizedPart, index)}
      </Fragment>
    );
  }

  if (
    normalizedPart.type === "dynamic-tool" &&
    normalizedPart.toolName === "propose_memory_write"
  ) {
    const resultText =
      normalizedPart.state === "output-available" &&
      typeof normalizedPart.output === "string"
        ? normalizedPart.output
        : null;
    return (
      <MemoryProposalCard
        key={index}
        toolUseId={String(normalizedPart.toolCallId ?? "")}
        input={normalizedPart.input}
        resultText={resultText}
        isError={normalizedPart.state === "output-error"}
      />
    );
  }

  if (normalizedPart.type === "dynamic-tool") {
    return (
      <ToolCallCard
        key={index}
        name={normalizedPart.toolName ?? "unknown"}
        input={normalizedPart.input}
        result={getToolFallbackResult(normalizedPart)}
        state={normalizedPart.state ?? "input-available"}
      />
    );
  }

  return null;
}
